import { useEffect, useRef, useState } from 'react'
import { contentAPI } from '../services/api'
import { apiErrorMessage } from '../utils/apiError'
import { NetworkPicks, PackDone, type PackResult, writePack } from './ContentPack'

/** About two minutes of talking (the backend's OWN_WORDS_MAX). */
export const OWN_WORDS_MAX = 4000
const MAX_SECONDS = 120

// The browser's speech recognition (Safari on iPhone, Chrome): typed loosely,
// as it isn't in TypeScript's DOM types.
interface Recognition {
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null
  onerror: ((e: { error: string }) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
}
type RecognitionCtor = new () => Recognition

export const speechRecognition = (): RecognitionCtor | null => {
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor }
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

/**
 * Create → Talk it out: say what you think (or type it), and get a post in
 * your own words. The browser turns speech into text as you talk; nothing is
 * recorded or uploaded, only the words you keep. The post adds nothing you
 * didn't say, and is checked like every other before it can go out.
 */
const TalkItOut = () => {
  const Ctor = speechRecognition()
  const [words, setWords] = useState('')
  const [listening, setListening] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [networks, setNetworks] = useState<string[]>(['twitter'])
  const [writing, setWriting] = useState(false)
  const [drafted, setDrafted] = useState<PackResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const recognition = useRef<Recognition | null>(null)
  const before = useRef('') // the words there before this round of talking

  useEffect(() => {
    if (!listening) return
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(timer)
  }, [listening])

  useEffect(() => {
    if (listening && seconds >= MAX_SECONDS) recognition.current?.stop()
  }, [listening, seconds])

  useEffect(() => () => recognition.current?.stop(), [])

  const start = () => {
    if (!Ctor) return
    setError(null)
    setDrafted(null)
    before.current = words ? `${words.trim()} ` : ''
    const r = new Ctor()
    r.continuous = true
    r.interimResults = true
    r.lang = navigator.language || 'en-US'
    // Rebuilt from every result each time, finished and in progress: some
    // browsers send finished results again, which appending would double.
    r.onresult = (e) => {
      const heard = Array.from(e.results)
        .map((result) => result[0].transcript.trim())
        .filter(Boolean)
        .join(' ')
      setWords((before.current + heard).slice(0, OWN_WORDS_MAX))
    }
    r.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed')
        setError('The microphone is blocked for this site. Allow it in your browser, or type instead.')
      else if (e.error !== 'no-speech' && e.error !== 'aborted') setError("Couldn't hear that. Try again, or type instead.")
    }
    r.onend = () => setListening(false)
    recognition.current = r
    setSeconds(0)
    setListening(true)
    r.start()
  }

  const stop = () => recognition.current?.stop()

  const write = async () => {
    setWriting(true)
    setError(null)
    try {
      setDrafted(
        await writePack(
          networks,
          (contentType) => contentAPI.generate({ content_type: contentType, own_words: words.trim() }),
          (e) => apiErrorMessage(e, "Couldn't write it.")
        )
      )
    } finally {
      setWriting(false)
    }
  }

  return (
    <section aria-label="Talk it out" className="rounded-lg bg-white p-4 shadow">
      <h2 className="text-lg font-semibold text-gray-900">Talk it out</h2>
      <p className="text-sm text-gray-600">
        {Ctor
          ? 'Say what you think, like you would to a friend. You get a post in your own words.'
          : 'Type what you think, like you would say it. You get a post in your own words.'}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {Ctor &&
          (listening ? (
            <button
              type="button"
              onClick={stop}
              className="rounded-full bg-danger-700 px-4 py-2 text-sm font-medium text-white"
            >
              Stop · {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
            </button>
          ) : (
            <button
              type="button"
              onClick={start}
              className="rounded-full bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              {words ? 'Keep talking' : 'Start talking'}
            </button>
          ))}
        {listening && <span className="text-xs text-gray-500">Listening… stops by itself after 2 minutes.</span>}
      </div>
      <textarea
        aria-label="Your words"
        value={words}
        onChange={(e) => setWords(e.target.value.slice(0, OWN_WORDS_MAX))}
        rows={5}
        placeholder={Ctor ? 'Your words appear here as you talk. Fix anything it misheard.' : 'What would you say?'}
        className="mt-3 w-full rounded-md border border-gray-300 p-2 text-sm"
      />
      <div className="mt-2 space-y-2">
        <NetworkPicks picked={networks} onChange={setNetworks} />
        <button
          type="button"
          onClick={write}
          disabled={writing || listening || words.trim().length < 20 || networks.length === 0}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {writing ? 'Writing…' : networks.length > 1 ? `Turn it into ${networks.length} posts` : 'Turn it into a post'}
        </button>
        {drafted && <PackDone result={drafted} />}
      </div>
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-700">
          {error}
        </p>
      )}
    </section>
  )
}

export default TalkItOut
